<template>
  <div class="questionario-container">
    <transition name="fade" mode="out-in">
      <div v-if="perguntaAtual" :key="`pergunta-${perguntaAtual.cd_pergunta}`">
        <CabecalhoPergunta :pergunta-atual="perguntaAtual" />

        <div v-if="componenteRespostaAtual">
          <component
            :disabled="enviando"
            :model-value="resposta"
            :pergunta="perguntaAtual"
            :is="componenteRespostaAtual"
            :quantidadeMinimaCaracteres="perguntaAtual.qt_minima_caracter"
            @update:modelValue="resposta = $event"
            @resposta-valida="handleValidacaoResposta"
            :key="perguntaAtual && perguntaAtual.cd_pergunta"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="js">
import CabecalhoPergunta from "./CabecalhoPergunta.vue";
import { getRespostaComponent } from "./respostas/registry";
import { questionarioAdapter } from "../adapters/questionarioAdapter";

export default {
  name: "QuestionarioForm",

  components: {
    CabecalhoPergunta
  },

  props: {
    cdPessoa: {
      type: [String, Number],
      required: true
    },
    questionarioData: {
      type: Object,
      default: null
    },
    titulo: {
      type: String,
      default: "Questionario de acompanhamento"
    }
  },

  data() {
    return {
      erro: null,
      enviando: false,
      mostrarSucesso: false,
      perguntas: [],
      respostas: {},
      indexAtual: 0,
      resposta: "",
      validacaoResposta: {
        valida: false
      }
    };
  },

  watch: {
    perguntaAtual(novaPergunta) {
      if (!novaPergunta) {
        this.resposta = "";
        this.emitirEstadoFormulario();
        return;
      }

      const respostaSalva = this.respostas[novaPergunta.cd_pergunta];

      if (Array.isArray(respostaSalva)) {
        this.resposta = [...respostaSalva];
        this.emitirEstadoFormulario();
        return;
      }

      this.resposta = respostaSalva || this.valorInicialResposta(novaPergunta);
      this.emitirEstadoFormulario();

      this.validacaoResposta.valida = false;
    },

    resposta() {
      this.emitirEstadoFormulario();
    },

    questionarioData: {
      immediate: true,
      handler(novoQuestionario) {
        if (!novoQuestionario) {
          return;
        }

        this.processarDadosQuestionario([novoQuestionario]);
      }
    },

    enviando() {
      this.emitirEstadoFormulario();
    }
  },

  computed: {
    perguntaAtual() {
      return this.perguntas[this.indexAtual] || null;
    },

    totalPerguntas() {
      if (!this.perguntaAtual) {
        return 0;
      }

      return Number(this.perguntaAtual.total_perguntas || 1);
    },

    numeroPerguntaAtual() {
      if (!this.perguntaAtual) {
        return 0;
      }

      return Number(this.perguntaAtual.pergunta_atual || 1);
    },

    isPrimeiraPergunta() {
      return this.numeroPerguntaAtual === 1;
    },

    isUltimaPergunta() {
      if (!this.perguntaAtual) {
        return false;
      }

      if (this.perguntaAtual.ultima_pergunta) {
        return true;
      }
      return this.numeroPerguntaAtual === this.totalPerguntas;
    },

    progressoPercentual() {
      const porcentagemBackend = Number(this.perguntaAtual?.porcentagem);

      if (!Number.isNaN(porcentagemBackend)) {
        return porcentagemBackend;
      }

      if (this.totalPerguntas === 0) return 0;
      return Math.round((this.numeroPerguntaAtual / this.totalPerguntas) * 100);
    },

    respostaValida() {
      if (this.isUltimaPergunta) {
        return true;
      }

      const obrigatorio = ["O", true].includes(this.perguntaAtual?.obrigatorio);
      const obrigatorioEspecial = this.perguntaAtual?.obrigatorio === "E";

      if (this.perguntaAtual?.tipo_resposta === "D") {
        if (Number(this.perguntaAtual.cd_pergunta) === 1) {
          return this.resposta && String(this.resposta).trim() !== "";
        }

        if (obrigatorioEspecial) {
          return true;
        }

        return obrigatorio ? this.validacaoResposta.valida : true;
      }

      if (this.perguntaAtual?.tipo_resposta === "L") {
        if (!this.resposta || String(this.resposta).trim() === "") {
          return !obrigatorio;
        }
        if (obrigatorio) {
          const dimensoes = this.perguntaAtual?.dimensoes || [];
          const answeredCount = this.resposta.split("|").length / 2;
          return dimensoes.length > 0 && answeredCount >= dimensoes.length;
        }
        return true;
      }

      if (obrigatorio) {
        if (Array.isArray(this.resposta)) {
          return this.resposta.length > 0;
        }

        return (
          this.resposta !== null &&
          this.resposta !== undefined &&
          String(this.resposta).trim() !== ""
        );
      }

      if (Array.isArray(this.resposta)) {
        return this.resposta.length > 0;
      }

      return (
        this.resposta !== null &&
        this.resposta !== undefined &&
        String(this.resposta).trim() !== ""
      );
    },

    deveBloquearProximo() {
      if (this.isUltimaPergunta) {
        return false;
      }

      return !this.respostaValida;
    },

    respostasSelecionadas() {
      if (!Array.isArray(this.perguntaAtual?.respostas)) {
        return [];
      }

      const valoresSelecionados = Array.isArray(this.resposta)
        ? this.resposta
        : [this.resposta];

      return this.perguntaAtual.respostas.filter(opcao =>
        valoresSelecionados.some(
          valor => String(valor) === String(opcao.cd_resposta)
        )
      );
    },

    quantidadeRespostaSelecionada() {
      const resposta = this.resposta;

      if (Array.isArray(resposta)) {
        return resposta.length;
      }

      if (
        this.perguntaAtual?.tipo_resposta === "L" &&
        typeof resposta === "string"
      ) {
        return this.getRespostasLikert().length;
      }

      return 0;
    },

    bloquearProximoPorObrigatorioEspecial() {
      return this.deveBloquearProximo;
    },

    respostaTextoDestaque() {
      if (this.perguntaAtual?.tipo_resposta !== "D") {
        return "";
      }

      if (!Array.isArray(this.perguntaAtual?.respostas)) {
        return "";
      }

      return this.perguntaAtual.respostas[0]?.ds_resposta || "";
    },

    componenteRespostaAtual() {
      if (!this.perguntaAtual?.tipo_resposta) {
        return null;
      }
      return getRespostaComponent(
        this.perguntaAtual.tipo_resposta,
        this.perguntaAtual
      );
    }
  },

  methods: {
    validarQuantidadeResposta() {
      const pergunta = this.perguntaAtual;

      if (!pergunta) {
        return { valida: true, mensagem: "" };
      }

      const minimo = Number(pergunta.qt_minima_resposta || 0);
      const maximo = Number(pergunta.qt_maxima_resposta || 0);

      if (minimo <= 0 && maximo <= 0) {
        return { valida: true, mensagem: "" };
      }

      if (!Array.isArray(this.resposta) && pergunta.tipo_resposta !== "L") {
        return { valida: true, mensagem: "" };
      }

      const quantidade = this.quantidadeRespostaSelecionada;

      if (maximo > 0 && quantidade > maximo) {
        return {
          valida: false,
          mensagem: `Você pode selecionar no máximo ${maximo} ${maximo === 1 ? "opção" : "opções"}.`
        };
      }

      if (minimo > 0 && quantidade < minimo) {
        return {
          valida: false,
          mensagem: `Selecione pelo menos ${minimo} ${minimo === 1 ? "opção" : "opções"}.`
        };
      }

      return { valida: true, mensagem: "" };
    },

    handleValidacaoResposta(dados) {
      this.validacaoResposta.valida = Boolean(dados?.valida);
      this.$emit("resposta-valida", dados);
      this.emitirEstadoFormulario();
    },

    processarDadosQuestionario(perguntas) {
      this.perguntas = questionarioAdapter.mapPerguntas(perguntas);
      this.indexAtual = 0;
      this.resposta = this.perguntaAtual
        ? this.valorInicialResposta(this.perguntaAtual)
        : "";
      this.emitirEstadoFormulario();
    },

    valorInicialResposta(pergunta) {
      if (!pergunta) {
        return "";
      }

      if (pergunta.tipo_resposta === "D" && pergunta.respostas?.length) {
        const respostaSelecionada = pergunta.respostas.find(
          opcao => opcao.selecionada
        );

        if (Number(pergunta.cd_pergunta) === 1) {
          return (
            respostaSelecionada?.cd_resposta ||
            pergunta.respostas[0]?.cd_resposta ||
            respostaSelecionada?.ds_resposta ||
            pergunta.respostas[0]?.ds_resposta ||
            ""
          );
        }

        if (respostaSelecionada) {
          return respostaSelecionada.ds_resposta || "";
        }

        return "";
      }

      if (pergunta.tipo_resposta === "C") {
        return pergunta.respostas
          .filter(opcao => opcao.selecionada)
          .map(opcao => opcao.cd_resposta);
      }

      if (pergunta.tipo_resposta === "M") {
        return pergunta.respostas
          .filter(opcao => opcao.selecionada)
          .map(opcao => opcao.cd_resposta);
      }

      if (pergunta.tipo_resposta === "L") {
        const partes = [];
        for (const dim of pergunta.dimensoes || []) {
          const selecionada = dim.respostas?.find(r => r.selecionada);
          if (selecionada) {
            partes.push(dim.cd_pergunta, String(selecionada.cd_resposta));
          }
        }
        return partes.join("|");
      }

      const selecionada = pergunta.respostas.find(opcao => opcao.selecionada);
      return selecionada ? selecionada.cd_resposta : "";
    },

    salvarRespostaAtual() {
      if (!this.perguntaAtual) {
        return;
      }

      this.respostas[this.perguntaAtual.cd_pergunta] = Array.isArray(
        this.resposta
      )
        ? [...this.resposta]
        : this.resposta;
    },

    avancarPergunta() {
      if (
        this.indexAtual >= this.perguntas.length - 1 ||
        !this.respostaValida
      ) {
        return;
      }

      this.salvarRespostaAtual();
      this.indexAtual++;
      this.emitirEstadoFormulario();
    },

    voltarPergunta() {
      if (this.indexAtual <= 0) {
        return;
      }

      delete this.respostas[this.perguntaAtual.cd_pergunta];
      this.indexAtual--;
      this.emitirEstadoFormulario();
    },

    atualizarCheckbox(event) {
      const valor = event.target.value;
      const respostasSelecionadas = Array.isArray(this.resposta)
        ? [...this.resposta]
        : [];

      if (event.target.checked) {
        if (!respostasSelecionadas.includes(valor)) {
          respostasSelecionadas.push(valor);
        }
      } else {
        this.resposta = respostasSelecionadas.filter(item => item !== valor);
        return;
      }

      this.resposta = respostasSelecionadas;
    },

    checkboxMarcado(cdResposta) {
      return Array.isArray(this.resposta) && this.resposta.includes(cdResposta);
    },

    emitirEstadoFormulario() {
      const percent =
        this.perguntaAtual?.porcentagem !== undefined &&
          this.perguntaAtual?.porcentagem !== null
          ? Number(this.perguntaAtual.porcentagem)
          : this.progressoPercentual;

      this.$emit("form-state-change", {
        current: this.numeroPerguntaAtual,
        total: this.totalPerguntas,
        percent: Number.isNaN(percent) ? this.progressoPercentual : percent,
        isPrimeiraPergunta: this.isPrimeiraPergunta,
        isUltimaPergunta: this.isUltimaPergunta,
        respostaValida: this.respostaValida,
        bloquearProximo: this.deveBloquearProximo,
        enviando: this.enviando
      });
    },

    async enviarRespostas() {
      const payload = this.getRespostaAtualPayload();
      this.$emit("submit-resposta", payload);
    },

    formatarRespostaAtual() {
      if (!this.perguntaAtual) {
        return null;
      }

      if (
        this.perguntaAtual.tipo_resposta === "D" &&
        Number(this.perguntaAtual.cd_pergunta) === 1
      ) {
        return (
          this.resposta ||
          this.perguntaAtual.respostas?.find(opcao => opcao.selecionada)
            ?.cd_resposta ||
          this.perguntaAtual.respostas?.[0]?.cd_resposta ||
          null
        );
      }

      if (
        this.perguntaAtual.tipo_resposta === "D" &&
        Number(this.perguntaAtual.cd_pergunta) !== 1
      ) {
        return {
          cd_resposta: this.perguntaAtual.respostas?.[0]?.cd_resposta || null,
          ds_resposta: String(this.resposta || "").trim()
        };
      }

      if (this.perguntaAtual.tipo_resposta === "L") {
        return (
          this.perguntaAtual.respostas?.[0]?.cd_resposta || null
        );
      }

      return this.resposta;
    },

    getRespostasLikert() {
      const resposta = this.resposta;
      if (!resposta || typeof resposta !== "string" || resposta.trim() === "") {
        return [];
      }
      const parts = resposta.split("|");
      const result = [];
      for (let i = 0; i < parts.length - 1; i += 2) {
        const cdPergunta = parseInt(parts[i], 10);
        const cdResposta = parseInt(parts[i + 1], 10);
        if (!isNaN(cdPergunta) && !isNaN(cdResposta)) {
          result.push({ cdPergunta, cdResposta });
        }
      }
      return result;
    },

    getRespostaAtualPayload(options = {}) {
      const { incluirResposta = true } = options;

      this.salvarRespostaAtual();

      return {
        cdPergunta: this.perguntaAtual?.cd_pergunta || null,
        perguntaAtual: this.perguntaAtual?.pergunta_atual || null,
        cdResposta: incluirResposta ? this.formatarRespostaAtual() : null
      };
    },

    iniciarEnvio() {
      this.enviando = true;
      this.erro = null;
    },

    finalizarEnvioComErro(erro) {
      this.erro = erro || "Erro ao enviar respostas";
      this.$emit("erro", this.erro);
    },

    finalizarEnvioComSucesso() {
      this.enviando = false;
      this.mostrarSucesso = true;

      this.$emit("respostas-enviadas", {
        cdPessoa: this.cdPessoa,
        respostas: questionarioAdapter.mapRespostasPayload(this.respostas)
      });

      setTimeout(() => {
        this.mostrarSucesso = false;
      }, 2000);
    },

    limparRespostaAtual() {
      this.resposta = "";
      this.emitirEstadoFormulario();
    }
  },
};
</script>

<style scoped>
.questionario-container {
  width: 100%;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
}

.loading {
  text-align: center;
  padding: 32px 24px;
  color: #526176;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f0f3f7;
  border-top-color: #0d9b49;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-tentar {
  padding: 8px 14px;
  background: #d74646;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  margin-top: 8px;
}

.observacao {
  margin-bottom: 18px;
}

.resposta-destaque {
  padding: 18px 12px 24px;
  text-align: center;
}

.resposta-destaque p {
  margin: 0;
  color: #0da348;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.campo {
  margin-bottom: 20px;
}

.textarea,
.select {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #d8e1ea;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  box-sizing: border-box;
  background: #ffffff;
}

.textarea:focus,
.select:focus {
  outline: none;
  border-color: #0d9b49;
  box-shadow: 0 0 0 3px rgba(13, 155, 73, 0.12);
}

.textarea {
  resize: vertical;
  line-height: 1.5;
}

.opcao {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid #e4ebf1;
  border-radius: 12px;
}

.radio,
.checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.opcao label {
  cursor: pointer;
  color: #445b78;
  font-size: 14px;
  margin: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
